import type { ContextMap, ContextMapBlock, ContextMapKind } from '@/types/chat';

const KINDS = new Set<ContextMapKind>([
  'system',
  'tools',
  'memory',
  'chat',
  'result',
]);

/** Plain-language kind names for business owners, in place of the component's defaults. */
export const CONTEXT_MAP_KIND_LABELS: Record<ContextMapKind, string> = {
  system: "Connie's instructions",
  tools: 'Tools',
  memory: 'Customer summary',
  chat: 'Conversation',
  result: 'Data Connie looked up',
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const count = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : null;

const text = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const readBlock = (value: unknown): ContextMapBlock | null => {
  const record = asRecord(value);
  if (!record) return null;
  const id = text(record.id);
  const kind = record.kind as ContextMapKind;
  const tokens = count(record.tokens);
  if (!id || !KINDS.has(kind) || tokens === null) return null;
  const detail = text(record.detail);
  const items = Array.isArray(record.items)
    ? record.items
        .map(item => text(asRecord(item)?.label))
        .filter(Boolean)
        .map(label => ({ label }))
    : undefined;
  return {
    id,
    kind,
    label: text(record.label) || CONTEXT_MAP_KIND_LABELS[kind],
    tokens,
    ...(detail ? { detail } : {}),
    ...(items?.length ? { items } : {}),
  };
};

/**
 * `contextMap` as FSI sends it on the stream's done event (FSI #1224): what filled
 * Connie's context window on the turn's last model call. Malformed blocks are dropped;
 * a map without a window size or blocks is left out.
 */
export function parseContextMap(value: unknown): ContextMap | undefined {
  const record = asRecord(value);
  if (!record) return undefined;
  const limit = count(record.limit);
  const used = count(record.used);
  const blocks = Array.isArray(record.blocks)
    ? record.blocks
        .map(readBlock)
        .filter((block): block is ContextMapBlock => block !== null)
    : [];
  if (!limit || used === null || !blocks.length) return undefined;
  const ids = new Set(blocks.map(block => block.id));
  const trimmed = Array.isArray(record.trimmed)
    ? record.trimmed.map(text).filter(id => ids.has(id))
    : [];
  return {
    model: text(record.model),
    limit,
    used,
    blocks,
    ...(trimmed.length ? { trimmed } : {}),
  };
}

/** "claude-sonnet-5-5" → "Sonnet 5.5"; a date suffix is dropped. */
export function contextMapModelName(model: string): string {
  const parts = model
    .replace(/^anthropic\./, '')
    .replace(/^claude-/, '')
    .split('-')
    .filter(part => part && !/^\d{8}$/.test(part));
  if (!parts.length) return model;
  const [family, ...version] = parts;
  const name = family.charAt(0).toUpperCase() + family.slice(1);
  return version.length ? `${name} ${version.join('.')}` : name;
}

/** Share of the window in use, as a whole percent. */
export const contextMapPercent = (map: ContextMap) =>
  Math.round((map.used / map.limit) * 100);
